
"use client";

import { cn } from "@/lib/utils";
import React, { useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined
);

export const useModernSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useModernSidebar must be used within a ModernSidebarProvider");
  }
  return context;
};

export const ModernSidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  const [openState, setOpenState] = useState(false);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const ModernSidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <ModernSidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </ModernSidebarProvider>
  );
};

interface SidebarBodyProps {
  className?: string;
  children?: React.ReactNode;
}

export const ModernSidebarBody = ({ className, children }: SidebarBodyProps) => {
  return (
    <>
      <DesktopSidebar className={className}>{children}</DesktopSidebar>
      <MobileSidebar className={className}>{children}</MobileSidebar>
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => {
  const { open, setOpen, animate } = useModernSidebar();
  return (
    <motion.div
      className={cn(
        "h-full px-4 py-4 bg-slate-900 dark:bg-slate-900 w-[300px] flex-shrink-0",
        // Note: hidden/flex classes must come AFTER to prevent override
        "hidden md:flex md:flex-col"
      )}
      animate={{
        width: animate ? (open ? "300px" : "60px") : "300px",
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className={cn("flex flex-col h-full", className)}>
        {children}
      </div>
    </motion.div>
  );
};

export const MobileSidebar = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => {
  const { open, setOpen } = useModernSidebar();
  return (
    <>
      <div
        className="h-14 px-4 py-2 flex flex-row md:hidden items-center justify-between bg-slate-900 dark:bg-slate-900 w-full fixed top-0 left-0 right-0 z-30"
      >
        {/* Logo on the left */}
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="/logo.png" 
            alt="SleepNest" 
            className="h-8 w-8"
          />
          <span className="text-white font-semibold text-lg">SleepNest</span>
        </Link>
        
        {/* Menu button on the right */}
        <button 
          onClick={() => setOpen(!open)}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="text-white h-5 w-5" />
        </button>
        
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className={cn(
                "fixed h-full w-full inset-0 bg-slate-900 dark:bg-slate-900 p-6 z-[100] flex flex-col justify-between",
                className
              )}
            >
              <div
                className="absolute right-6 top-6 z-50 text-white dark:text-white cursor-pointer p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                onClick={() => setOpen(!open)}
              >
                <X className="h-5 w-5" />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="h-14 md:hidden">
        {/* This is a spacer for mobile to prevent content from being hidden under the fixed navbar */}
      </div>
    </>
  );
};

export const ModernSidebarLink = ({
  to,
  icon,
  label,
  active,
  className,
  onClick,
  external,
  ...props
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  className?: string;
  onClick?: () => void;
  external?: boolean;
  [key: string]: any;
}) => {
  const { open, animate } = useModernSidebar();
  
  const linkClassName = cn(
    "flex items-center justify-start gap-2 group/sidebar py-2",
    active && "text-primary font-medium",
    className
  );
  
  const content = (
    <>
      {icon}
      <motion.span
        animate={{
          display: animate ? (open ? "inline-block" : "none") : "inline-block",
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-slate-200 dark:text-slate-200 text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
      >
        {label}
      </motion.span>
    </>
  );
  
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
        onClick={onClick}
        {...props}
      >
        {content}
      </a>
    );
  }
  
  return (
    <Link
      to={to}
      className={linkClassName}
      onClick={onClick}
      {...props}
    >
      {content}
    </Link>
  );
};
